import { useEffect, useState } from "react";
import { X, Radar, Search, ChevronDown, Ban, IndianRupee } from "lucide-react";
import { toast } from "sonner";
import { appApi, inr, fmtDate, fmtDateTime, formatApiErrorDetail } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { StatusTabs } from "@/components/StatusTabs";
import { TrackingDetail } from "@/components/TrackingDetail";

const TYPE_TABS = [
  { k: "all", l: "All Clients' Orders" },
  { k: "amazon-b2c", l: "Amazon B2C" },
  { k: "b2b", l: "B2B Freight" },
];

const clientName = (o) => o.merchant?.name || o.merchant_name || "—";
const clientMeta = (o) => o.merchant?.email || o.merchant?.business || o.merchant_email || "";

export default function AdminOrders() {
  const [tab, setTab] = useState("all");
  const [statusFilter, setStatusFilter] = useState("");
  const [orders, setOrders] = useState(null);
  const [trackingAwb, setTrackingAwb] = useState(null);
  const [trackingDetail, setTrackingDetail] = useState(null);
  const [trackingError, setTrackingError] = useState("");
  const [trackingLoading, setTrackingLoading] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [found, setFound] = useState(undefined); // undefined=idle, null=not found
  const [menuFor, setMenuFor] = useState(null);
  const [actBusy, setActBusy] = useState("");

  const findOrder = async (e) => {
    e.preventDefault();
    const q = searchQ.trim();
    if (q.length < 3) {
      toast.error("Type at least 3 characters — AWB, order ID or tracking number.");
      return;
    }
    setFound(undefined);
    setActBusy("search");
    try {
      const d = await appApi.get(`admin/orders/${encodeURIComponent(q)}/find`);
      setFound(d.order || d);
    } catch (err) {
      setFound(null);
      toast.info(formatApiErrorDetail(err.response?.data?.detail) || "No order matches that.");
    } finally {
      setActBusy("");
    }
  };

  const cancelOrder = async (o, e) => {
    e.stopPropagation();
    setMenuFor(null);
    const refund = window.confirm(`Cancel order ${o.awb}?\n\nOK = cancel WITH wallet refund of ${inr(o.rate)}\nCancel = stop here`);
    if (!refund) return;
    setActBusy(o.id);
    try {
      await appApi.post(`admin/orders/${o.id}/cancel`, { refund: true });
      toast.success(`Order ${o.awb} cancelled and wallet refunded.`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setActBusy("");
    }
  };

  const editCharge = async (o, e) => {
    e.stopPropagation();
    setMenuFor(null);
    const val = window.prompt(`New chargeable amount (₹) for ${o.awb} — current rate ${inr(o.rate)}:`, o.rate ?? "");
    if (val == null || val.trim() === "") return;
    const n = parseFloat(val);
    if (!(n >= 0)) {
      toast.error("Enter a valid amount.");
      return;
    }
    setActBusy(o.id);
    try {
      await appApi.put(`admin/orders/${o.id}/charge`, { new_charge_inr: n });
      toast.success(`Charge for ${o.awb} updated to ${inr(n)}.`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setActBusy("");
    }
  };

  const load = () => {
    setOrders(null);
    if (tab === "all") {
      Promise.allSettled([
        appApi.get("admin/orders/amazon-b2c", statusFilter ? { status_filter: statusFilter } : {}),
        appApi.get("admin/orders/b2b", statusFilter ? { status_filter: statusFilter } : {}),
      ]).then(([a, b]) => {
        const merged = [
          ...(a.status === "fulfilled" ? a.value.orders || [] : []),
          ...(b.status === "fulfilled" ? b.value.orders || [] : []),
        ].sort((x, y) => new Date(y.created_at) - new Date(x.created_at));
        setOrders(merged);
      });
      return;
    }
    appApi
      .get(`admin/orders/${tab}`, statusFilter ? { status_filter: statusFilter } : {})
      .then((d) => setOrders(d.orders || []))
      .catch(() => setOrders([]));
  };
  useEffect(load, [tab, statusFilter]);

  const openTracking = async (o) => {
    setTrackingAwb(o.awb);
    setTrackingDetail(null);
    setTrackingError("");
    setTrackingLoading(true);
    try {
      const d = await appApi.get(`track/${encodeURIComponent(o.awb)}`);
      setTrackingDetail(d);
    } catch (e) {
      setTrackingError(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setTrackingLoading(false);
    }
  };

  return (
    <div data-testid="admin-orders-page" className="space-y-6">
      <div>
        <p className="text-xs font-mono font-bold uppercase tracking-[0.2em] text-blaze">Admin</p>
        <h1 className="font-display font-black tracking-tight text-3xl sm:text-4xl mt-1">All Clients' Orders</h1>
        <p className="text-sm text-zinc-500 mt-2">Every B2C and B2B shipment with client name and in-panel tracking — click any row.</p>
      </div>

      <div className="flex flex-wrap items-center gap-4">
        <div className="flex gap-0 border-2 border-obsidian w-fit bg-white flex-wrap" data-testid="admin-orders-tabs">
          {TYPE_TABS.map((t) => (
            <button key={t.k} onClick={() => setTab(t.k)} data-testid={`admin-orders-tab-${t.k}`}
              className={`px-5 py-2.5 text-xs font-mono font-bold uppercase tracking-widest transition-colors duration-150 ${tab === t.k ? "bg-obsidian text-sky-300" : "hover:bg-acid/40"}`}>
              {t.l}
            </button>
          ))}
        </div>
        <StatusTabs
          value={statusFilter || "all"}
          onChange={(v) => setStatusFilter(v === "all" ? "" : v)}
          prefix="admin-orders"
        />
      </div>

      <form onSubmit={findOrder} className="flex items-center gap-2 flex-wrap" data-testid="admin-order-search-form">
        <div className="flex items-center gap-2 border-2 border-obsidian bg-white px-4 py-2.5 w-80">
          <Search className="w-4 h-4 text-zinc-400 shrink-0" />
          <input value={searchQ} onChange={(e) => setSearchQ(e.target.value)} placeholder="Find order — AWB / Order ID / tracking no." data-testid="admin-order-search"
            className="text-sm outline-none bg-transparent w-full" />
        </div>
        <button type="submit" disabled={actBusy === "search"} data-testid="admin-order-search-submit"
          className="text-[11px] font-mono font-bold uppercase tracking-widest px-4 py-2.5 border-2 border-obsidian bg-obsidian text-sky-300 disabled:opacity-60">
          {actBusy === "search" ? "Searching…" : "Find"}
        </button>
      </form>

      {found && (
        <div className="border-2 border-obsidian bg-white p-5 shadow-[6px_6px_0px_0px_#2563EB]" data-testid="admin-order-found">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <p className="font-mono text-sm font-bold flex items-center gap-2"><Radar className="w-4 h-4 text-acid" /> {found.awb}</p>
              <p className="text-xs text-zinc-500 mt-1">
                {found.merchant?.name || found.merchant_name || ""} · {found.courier_name} · {found.pickup?.pincode} → {found.delivery?.pincode} · {inr(found.rate)} · booked {fmtDateTime(found.created_at)}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <StatusBadge status={found.status} />
              <button onClick={() => openTracking(found)} data-testid="admin-order-found-track"
                className="text-[11px] font-mono font-bold uppercase tracking-widest px-4 py-2 border-2 border-obsidian bg-acid text-white">
                Track
              </button>
              <button onClick={() => setFound(undefined)} aria-label="Dismiss found order" data-testid="admin-order-found-close"
                className="p-2 border-2 border-obsidian hover:bg-acid hover:text-white transition-colors duration-150"><X className="w-4 h-4" /></button>
            </div>
          </div>
        </div>
      )}

      <div className="border-2 border-obsidian bg-white overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]" data-testid="admin-orders-table">
          <thead>
            <tr className="border-b-2 border-obsidian text-left">
              {["AWB", "Client", "Type", "Courier", "Route", "Rate", "Status", "Created", ""].map((h) => (
                <th key={h} className="px-4 py-3 text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(orders || []).map((o) => (
              <tr key={o.id || o.awb}
                onClick={() => openTracking(o)}
                className="border-b border-zinc-100 last:border-0 hover:bg-acid/20 transition-colors duration-100 cursor-pointer"
                data-testid={`admin-order-row-${o.awb}`}>
                <td className="px-4 py-3 font-mono text-xs font-bold">
                  <span className="flex items-center gap-1.5">{o.awb} <Radar className="w-3.5 h-3.5 text-acid" /></span>
                </td>
                <td className="px-4 py-3">
                  <p className="font-bold text-xs">{clientName(o)}</p>
                  <p className="text-[11px] font-mono text-zinc-500">{clientMeta(o)}</p>
                </td>
                <td className="px-4 py-3 font-mono text-xs uppercase">{o.shipment_type || "—"}</td>
                <td className="px-4 py-3 text-zinc-600">{o.courier_name}</td>
                <td className="px-4 py-3 font-mono text-xs text-zinc-500">{o.pickup?.pincode} → {o.delivery?.pincode}</td>
                <td className="px-4 py-3 font-mono text-xs font-bold">{o.rate != null ? inr(o.rate) : "—"}</td>
                <td className="px-4 py-3"><StatusBadge status={o.status} /></td>
                <td className="px-4 py-3 font-mono text-xs text-zinc-500">{fmtDate(o.created_at)}</td>
                <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                  <div className="relative">
                    <button onClick={() => setMenuFor(menuFor === (o.id || o.awb) ? null : (o.id || o.awb))} aria-label={`Admin actions for ${o.awb}`} data-testid={`admin-order-actions-${o.awb}`}
                      className="p-2 border-2 border-obsidian bg-white hover:bg-obsidian hover:text-sky-300 transition-colors duration-150">
                      <ChevronDown className="w-4 h-4" />
                    </button>
                    {menuFor === (o.id || o.awb) && (
                      <>
                        <div className="fixed inset-0 z-40" onClick={() => setMenuFor(null)} />
                        <div className="absolute right-0 top-full mt-1 z-50 w-56 border-2 border-obsidian bg-white shadow-[6px_6px_0px_0px_#0B1E3A] py-1" data-testid={`admin-order-menu-${o.awb}`}>
                          <button onClick={(e) => editCharge(o, e)} disabled={actBusy === o.id} data-testid={`admin-order-edit-charge-${o.awb}`}
                            className="w-full flex items-center gap-2 px-4 py-2.5 text-xs font-semibold text-zinc-700 hover:bg-acid/20 disabled:opacity-50">
                            <IndianRupee className="w-3.5 h-3.5" /> Edit chargeable amount
                          </button>
                          <button onClick={(e) => cancelOrder(o, e)} disabled={actBusy === o.id} data-testid={`admin-order-cancel-${o.awb}`}
                            className="w-full flex items-center gap-2 px-4 py-2.5 text-xs font-semibold text-red-600 hover:bg-red-50 disabled:opacity-50">
                            <Ban className="w-3.5 h-3.5" /> Cancel order + refund
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {orders && orders.length === 0 && <p className="px-5 py-10 text-sm text-zinc-500" data-testid="admin-orders-empty">No orders for this filter.</p>}
        {!orders && <p className="px-5 py-10 font-mono text-xs uppercase tracking-[0.3em] text-zinc-400">Loading orders…</p>}
      </div>
      {orders && (
        <p className="text-xs font-mono text-zinc-400 uppercase tracking-widest" data-testid="admin-orders-count">
          {orders.length} orders in view
        </p>
      )}

      {trackingAwb && (
        <div className="fixed inset-0 z-50" data-testid="admin-tracking-drawer">
          <div className="absolute inset-0 bg-obsidian/60" onClick={() => setTrackingAwb(null)} />
          <div className="absolute right-0 inset-y-0 w-full max-w-3xl bg-paper overflow-y-auto border-l-4 border-obsidian">
            <div className="sticky top-0 bg-white border-b-2 border-obsidian px-6 py-4 flex items-center justify-between z-10">
              <p className="font-mono text-sm font-bold">Tracking · {trackingAwb}</p>
              <button onClick={() => setTrackingAwb(null)} aria-label="Close tracking" data-testid="admin-tracking-close"
                className="p-2 border-2 border-obsidian hover:bg-acid hover:text-white transition-colors duration-150">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-6">
              {trackingLoading && <p className="font-mono text-xs uppercase tracking-[0.3em] text-zinc-400">Fetching courier status…</p>}
              {trackingError && (
                <div className="border-2 border-obsidian bg-white px-4 py-4 text-sm">
                  <p className="font-bold">{trackingError === "AWB not found" ? "Courier milestones not published yet" : trackingError}</p>
                  <p className="text-zinc-500 text-xs mt-1.5">Scans appear here once the courier picks up the parcel.</p>
                </div>
              )}
              {trackingDetail && <TrackingDetail detail={trackingDetail} />}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
